import React, { useState } from "react";
import locale from "@bonliva-crm/locale";
import { ErrorBoundary } from "@components/error-boundary";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { PayPeriod } from "@graphql";
import { PrimaryButton, RegularButton, WithTooltip } from "@common";
import { PriceListDay } from "@bonliva-crm/shared";

type Props = {
  onClose: () => void;
  onSubmit: (v: string[]) => void;
  open: boolean;
};

const options = [
  {
    v: PriceListDay.BusinessDay,
    l: locale.contractsPrice.priceListDay[PriceListDay.BusinessDay],
    d: "Välj detta alternativ när du vill specificera lön/ersättning/pris för vardagar"
  },
  {
    v: PriceListDay.SqueezedDay,
    l: locale.contractsPrice.priceListDay[PriceListDay.SqueezedDay],
    d: "Klämdag definieras i enlighet med SKR: ’Tid från kl. 00.00 till kl. 24.00 på dag som både föregås och efterföljs av söndag eller helgdag, varvid jul- och nyårsafton jämställs med helgdag."
  },
  {
    v: PriceListDay.Weekends,
    l: locale.contractsPrice.priceListDay[PriceListDay.Weekends],
    d: "Med helger menas lördag och söndag. Observera att helger inte innefattar helgdagar (röda dagar) utan dessa måste specificeras specifikt separat."
  },
  {
    v: PriceListDay.Holiday,
    l: locale.contractsPrice.priceListDay[PriceListDay.Holiday],
    d: "Med helgdag menas röda dagar. Observera att du själv måste fylla i vilka specifika dagar som du vill att helgdagspriset ska gälla för. Detta gör du i den föregående vyn under avtalstabellen."
  },
  {
    v: PriceListDay.MajorHoliday,
    l: locale.contractsPrice.priceListDay[PriceListDay.MajorHoliday],
    d: "Med storhelg menas de dagar där en högre lön/ersättning/pris gäller än för vanlig helgdag. Observera att du själv måste fylla i vilka specifika dagar som du vill att storhelgspriset ska gälla för. Detta gör du i den föregående vyn under avtalstabellen."
  },
  {
    v: PriceListDay.AnyTime,
    l: locale.contractsPrice.priceListDay[PriceListDay.AnyTime],
    d: "Alla dagar väljer du om alla dagar har samma lön/ersättning/pris” för en viss arbetstyp."
  },
  {
    v: locale.SpecificDaily,
    l: locale.SpecificDaily,
    d: "Detta alternativ väljer du om vill lägga in ett pris för enskilda veckodagar. Alternativt om du vill lägga in ett alternativ som gäller under en begränsad period för varje enskild veckodag. Ex:  07:00 - 19:00 varje måndag, tisdag och onsdag."
  },
  {
    v: PayPeriod.FullPeriod,
    l: locale.SpecificFullPeriod,
    d: `Om ett avtalsalternativ sträcker sig över flera dagar så lägger du in en hel period. Exempel:
        a. Måndag 07:00 - Fredag 19:00 (detta alternativ gäller för alla timmar under detta tidsintervall, inklusive kväll och natt)

        b. Dag före helgdag 16:00 - Vardag närmast efter helgdag (detta alternativ gäller för alla timmar under detta tidsintervall)`
  },
  {
    v: "freeInput",
    l: "Custom",
    d: `Detta alternativet tillåter fri redigering av samtliga fält.`
  }
];

export function DayTypesAddDialog({ onClose, open, onSubmit }: Props) {
  const [selected, setSelected] = useState([]);

  const handleChange = (e) => {
    const { value } = e.target;
    const newVals = selected.includes(value)
      ? selected.filter((x) => x !== value)
      : [...selected, value];
    setSelected(newVals);
  };

  const handleSubmit = () => {
    onSubmit(selected);
    onClose();
    setSelected([]);
  };

  return (
    open && (
      <ErrorBoundary>
        <Dialog open={open} onBackdropClick={onClose}>
          <DialogTitle>{locale.ChooseWorkTypesForContract}</DialogTitle>
          <DialogContent>
            <FormControl>
              <FormGroup>
                {options.map((o) => (
                  <FormControlLabel
                    key={o.v}
                    // disabled={selected.includes(o.value)}
                    control={
                      <Checkbox
                        checked={selected.includes(o.v)}
                        onChange={handleChange}
                        value={o.v}
                      />
                    }
                    label={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {o.l}
                        <WithTooltip placement={"right"} title={o.d}>
                          <InfoOutlined />
                        </WithTooltip>
                      </div>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </DialogContent>

          <DialogActions>
            <RegularButton onClick={onClose}>{locale.Cancel}</RegularButton>
            <PrimaryButton onClick={handleSubmit}>{locale.Add}</PrimaryButton>
          </DialogActions>
        </Dialog>
      </ErrorBoundary>
    )
  );
}
